import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { LanguageSwitcher, useI18n } from "@faceheart/i18n";
import { useAuth } from "../lib/auth/AuthProvider";
import { ApiError } from "../lib/api/client";

export function LoginPage() {
  const { status, login } = useAuth();
  const { t } = useI18n("admin");
  const loc = useLocation();
  // Login prefills are build-time env vars so a single codebase can ship
  // empty fields (dev / prod = shengyentech's own site) while prod-<client>
  // deployments keep a convenience prefill (set in the deploy workflow).
  // Unset → empty string, which is the dev/prod default.
  const [email, setEmail] = useState(import.meta.env.VITE_LOGIN_DEFAULT_EMAIL ?? "");
  const [password, setPassword] = useState("");
  const [orgCode, setOrgCode] = useState(import.meta.env.VITE_LOGIN_DEFAULT_ORG ?? "");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status === "authed") {
    const dest = (loc.state as { from?: string } | null)?.from ?? "/dashboard";
    return <Navigate to={dest} replace />;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await login(email, password, orgCode);
    } catch (ex) {
      if (ex instanceof ApiError) {
        if (ex.code === "account_locked") setErr(t("login.accountLocked"));
        else if (ex.code === "rate_limited") setErr(t("login.rateLimited"));
        else setErr(t("login.badCreds"));
      } else {
        setErr(t("login.networkFailed"));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-full bg-admin-bg bg-tech-grid text-admin-text1 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex justify-end mb-3">
          <LanguageSwitcher />
        </div>
        <div className="text-center mb-8">
          <div className="font-mono-display text-admin-cyan text-3xl tracking-wider">
            FaceHeart
          </div>
          <div className="text-admin-text3 text-sm mt-1">Admin Console</div>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-admin-bg2 border border-admin-border rounded-lg p-6 space-y-4"
        >
          <Field label={t("login.orgCode")}>
            <input
              required
              value={orgCode}
              onChange={(e) => setOrgCode(e.target.value)}
              className={inputCls}
            />
          </Field>

          <Field label={t("login.email")}>
            <input
              required
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls}
            />
          </Field>

          <Field label={t("login.password")}>
            <input
              required
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
            />
          </Field>

          {err && (
            <div className="text-admin-red text-sm bg-admin-red/10 border border-admin-red/30 rounded p-2">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-admin-cyan text-admin-bg font-medium py-2.5 rounded hover:bg-admin-cyan/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {busy ? t("login.submitting") : t("login.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-admin-bg3 border border-admin-border rounded px-3 py-2 text-admin-text1 placeholder-admin-text3 focus:outline-none focus:border-admin-cyan transition-colors";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-admin-text2 text-xs uppercase tracking-wider">
        {label}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
